import { html, nothing, type PropertyValues } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import type { ButtonVariant } from '../button';
import { Component } from '../component';
import type { IconName } from '../icon';
import type { BannerActionPrimary } from './banner-action-primary';
import { styles } from './banner.styles';

import '../button';
import '../close-button';
import '../text';
import '../icon';

export type BannerVariant = 'neutral' | 'warning' | 'danger' | 'success';

function getIcon(variant?: BannerVariant): IconName {
	switch (variant) {
		case 'success': {
			return 'check';
		}
		case 'warning':
		case 'danger': {
			return 'attention';
		}
		case 'neutral':
		default: {
			return 'info-circle';
		}
	}
}

function getPrimaryActionVariant(variant?: BannerVariant): ButtonVariant {
	switch (variant) {
		case 'warning': {
			return 'warning';
		}
		case 'danger': {
			return 'danger';
		}
		case 'success':
		case 'neutral':
		default: {
			return 'primary';
		}
	}
}

export interface BannerProps {
	variant?: BannerVariant;
	dismissible?: boolean;
	dismissed?: boolean;
}

export class Banner extends Component {
	static override styles = [styles()];

	static override get properties() {
		return {
			variant: { type: String },
			dismissible: { type: Boolean },
			dismissed: { type: Boolean, reflect: true },
		};
	}

	/**
	 * Determines the style. Defaults to 'neutral'.
	 */
	declare variant: BannerProps['variant'];

	/**
	 * Determine if the banner is dismissible. It will not be dismissible if
	 * there's an action defined. Defaults to false.
	 */
	declare dismissible: BannerProps['dismissible'];

	/**
	 * Determine if the banner was dismissed or not.
	 */
	declare dismissed: BannerProps['dismissed'];

	/**
	 * Reference for the Primary Action
	 */
	#primaryAction?: BannerActionPrimary | null;

	constructor() {
		super();

		this.variant = 'neutral';
		this.dismissible = false;
		this.dismissed = false;
	}

	get primaryAction(): BannerActionPrimary | null {
		if (this.#primaryAction == null) {
			this.#primaryAction = this.querySelector('m-banner-action-primary');
		}

		return this.#primaryAction;
	}

	override updated(changedProperties: PropertyValues) {
		super.updated(changedProperties);

		// Force the primary action to have a variant based on the banner's variant
		if (changedProperties.has('variant') && this.primaryAction != null) {
			this.primaryAction.variant = getPrimaryActionVariant(this.variant);
		}
	}

	override render() {
		if (this.dismissed) {
			return nothing;
		}

		const hasDescription = this.querySelector(`*[slot='description']`) != null;
		const hasActions = this.querySelector(`*[slot='actions']`) != null;

		const closeButton = html`
			<m-close-button
				id="close"
				label="Dismiss"
				@click=${this.#handleDismissClick}
			></m-close-button>
		`;

		return html`
			<div
				class=${classMap({
					banner: true,
					'-success': this.variant === 'success',
					'-warning': this.variant === 'warning',
					'-danger': this.variant === 'danger',
					'-with-description': hasDescription,
				})}
			>
				<div class="wrapper">
					<slot name="icon">
						<m-icon name="${getIcon(this.variant)}"></m-icon>
					</slot>
					<div class="content">
						<slot name="title"></slot>
						<slot name="description"></slot>
					</div>
					${this.dismissible && !hasActions ? closeButton : nothing}
				</div>
				<slot name="actions"></slot>
			</div>
		`;
	}

	#handleDismissClick = () => {
		this.dismissed = true;

		this.emit('m-dismiss', { composed: true });
	};
}

window.customElements.define('m-banner', Banner);

declare global {
	interface HTMLElementTagNameMap {
		'm-banner': Banner;
	}
}
